import React, { useEffect, useRef } from 'react';
import { Link as RouterLink, To } from 'react-router-dom';

import { Box, Card } from '@mui/material';

import { COLOR_BORDER, COLOR_DARK_OVERLAY, COLOR_LIGHT_OVERLAY } from '~/components/App/theme';
import { ImageContextMenu } from '~/components/ImageContextMenu';
import { LoaderOverlay } from '~/components/Loader';
import { ImageRetry } from '~/components/Retry';
import { Tooltip } from '~/components/Tooltip';
import { ICamera } from '~/domain/ICamera';
import { IImageSettings } from '~/domain/IImageSettings';
import { ResponseError } from '~/domain/ResponseError';
import { SITE_CLIENT } from '~/env';
import { useContextMenu } from '~/hooks/useContextMenu';
import { useImageQuery } from '~/hooks/useImage';
import { useIntersection } from '~/hooks/useIntersectionObserver';
import { useRequestUrl } from '~/hooks/useRequestUrl';
import { useTrigger } from '~/hooks/useTrigger';
import * as LAYOUT from '~/pages/layout';
import { MakeURLFriendly } from '~/pages/urls';
import { useAppDispatch } from '~/redux';
import { addImageError } from '~/redux/features/imageErrorsSlice';

import {
  FEATURE_SHOT_IMAGE_SETTINGS,
  FEATURE_SHOT_PREVIEW_IMAGE_SETTINGS,
} from './imageSettings';

interface IVehicleImage {
  camera: ICamera;
  settings: IImageSettings;
}

export const VehicleImage = React.memo<IVehicleImage>(function VehicleImage(props) {
  const dispatch = useAppDispatch();
  const { camera, settings } = props;
  const { aspectRatio } = settings;
  const ref = useRef() as React.MutableRefObject<HTMLDivElement>;
  const [enabled, inViewHook] = useTrigger();
  useIntersection({ ref, callback: inViewHook.activate });
  const requestUrl = useRequestUrl();
  const {
    isFetching,
    isError,
    isInitialLoading,
    data: src,
    refetch,
    error,
  } = useImageQuery({
    settings,
    camera,
    query: { enabled },
  });
  const [anchorPosition, contextMenu] = useContextMenu({ isFetching, isError });

  const url = requestUrl({ camera: MakeURLFriendly(camera.name) });

  useEffect(() => {
    if (isError && error) {
      const respError = error as ResponseError;
      dispatch(addImageError(respError.message));
    }
  }, [isError, error, dispatch]);

  return (
    <>
      <Card
        sx={{ cursor: 'pointer' }}
        component={RouterLink as React.FC<{ to: To }>}
        to={!isError ? url : ''}
        onContextMenu={contextMenu.onContextMenu}
        id={camera.code}
      >
        <Box
          component="div"
          ref={ref}
          sx={{
            display: 'flex',
            position: 'relative',
            minWidth: 32,
            minHeight: 32 / aspectRatio,
            border: `1px solid ${COLOR_BORDER}`,
            borderRadius: '4px',
          }}
        >
          <Box
            sx={[
              (SITE_CLIENT==='Subaru') ? LAYOUT.CONTENTSUBARU : LAYOUT.CONTENT,
              {
                position: 'relative',
                paddingBottom: `${(1 / aspectRatio) * 100}%`,
              },
            ]}
          >

            {isError && <ImageRetry retry={refetch} />}
            {isFetching && <LoaderOverlay sx={{background: isInitialLoading ? COLOR_LIGHT_OVERLAY : COLOR_DARK_OVERLAY }} />}
            {src && (
              <>
                <Tooltip title={camera.name} placement={'top'} >
                  <Box sx={{position: 'absolute', left: 0, top: '30px', width: '100%', height: 'calc(100% - 30px)', zIndex: 100}} />
                </Tooltip>
                <Box 
                  component="img" 
                  src={src} 
                  alt={camera.name} 
                  sx={(SITE_CLIENT==='Subaru') ? LAYOUT.IMAGESUBARU : LAYOUT.IMAGE} 
                />
              </>
            )}
          </Box>
        </Box>
      </Card>
      <ImageContextMenu
        anchorPosition={anchorPosition}
        context={contextMenu}
        camera={camera}
        images={[
          { label: 'HD', settings: FEATURE_SHOT_IMAGE_SETTINGS },
          { label: '4K', settings: FEATURE_SHOT_PREVIEW_IMAGE_SETTINGS },
        ]}
      />
    </>
  );
});
